/*
 * Toolbox icons. Category icons and node icons are drawn from the Hi2 (Heroicons v2)
 * outline set so the palette reads as one family; a few node-specific glyphs come
 * from Lucide/Tabler where Hi2 has no close equivalent.
 */
import {
  HiOutlineArrowDownTray,
  HiOutlineChatBubbleLeftRight,
  HiOutlineBolt,
  HiOutlineCommandLine,
  HiOutlineArrowsRightLeft,
  HiOutlineVariable,
  HiOutlineChatBubbleBottomCenterText,
  HiOutlineCursorArrowRays,
  HiOutlineRectangleStack,
  HiOutlineGlobeAlt,
  HiOutlinePhoto,
  HiOutlineSparkles,
  HiOutlineBookOpen,
  HiOutlineSquares2X2,
  HiOutlineUserGroup,
  HiOutlineCpuChip,
  HiOutlineCloudArrowUp,
  HiOutlineShieldCheck,
  HiOutlineCodeBracket,
  HiOutlinePauseCircle,
  HiOutlineRectangleGroup,
  HiOutlineDocumentArrowUp,
} from "react-icons/hi2";
import { LuFunctionSquare, LuGitBranch } from "react-icons/lu";
import { TbSparkles as EditSparkIcon } from "react-icons/tb";
import { FaWandMagicSparkles as SparkIcon } from "react-icons/fa6";

import {
  AI_TASK_ACTION_NODE_TYPE,
  AGENT_ACTION_NODE_TYPE,
  AUTH_NODE_TYPE,
  BUTTONS_PROMPT_NODE_TYPE,
  CARDS_PROMPT_NODE_TYPE,
  CHOICE_NODE_TYPE,
  CODEHOOK_ACTION_NODE_TYPE,
  CODEHOOK_PROMPT_NODE_TYPE,
  CUSTOM_ACTION_NODE_TYPE,
  DOC_SEARCH_ACTION_NODE_TYPE,
  IMAGE_PROMPT_NODE_TYPE, INTEGRATION_ACTION_NODE_TYPE,
  JOURNEY_ACTION_NODE_TYPE,
  LANGUAGE_SELECTOR_PROMPT_NODE_TYPE,
  LIVE_AGENT_ACTION_NODE_TYPE,
  MESSAGE_PROMPT_NODE_TYPE,
  PAUSE_ACTION_NODE_TYPE,
  QUESTION_PROMPT_NODE_TYPE,
  WEBHOOK_ACTION_NODE_TYPE,
  DOC_CAPTURE_PROMPT_NODE_TYPE
} from "../common/const/node-types";
import { WORKFLOW_OPTION_SLOT } from "../common/const/settings-const";
import {GEN_AI_TEXT_TO_WF, GEN_AI_TEXT_TO_WF_EDIT} from "../../../consts/gen-ai-consts";
import { QUESTION_PROMPT } from "../common/const/settings-type";

export const toolboxFlowsTriggerButtons = [
  {
    id: "capture",
    title: "Input",
    icon: HiOutlineArrowDownTray,
    nodes: [
      {
        id: QUESTION_PROMPT_NODE_TYPE,
        text: "Question",
        icon: HiOutlineChatBubbleBottomCenterText,
        defaultConfig: {
          name: "Question",
          slot: '',
          displayText: '',
          displayTextOptions: [],
          type: 'text',
          autoFillFromEntity: true,
          responseModifyByAI: false
        }
      },
      {
        id: BUTTONS_PROMPT_NODE_TYPE,
        text: "Button",
        icon: HiOutlineCursorArrowRays,
        defaultConfig: {
          name: "Button",
          slot: '',
          displayText: '',
          displayTextOptions: [],
          data: [],
          type: 'buttons',
          autoFillFromEntity: true,
          responseModifyByAI: false,
        }
      },
      {
        id: CARDS_PROMPT_NODE_TYPE,
        text: "Cards",
        icon: HiOutlineRectangleStack,
        defaultConfig: {
          name: 'Cards',
          slot: WORKFLOW_OPTION_SLOT,
          displayText: '',
          displayTextOptions: [],
          data: [],
          type: 'cards',
          autoFillFromEntity: false,
          responseModifyByAI: false
        }
      },
      {
        id: DOC_CAPTURE_PROMPT_NODE_TYPE,
        text: "Document",
        icon: HiOutlineDocumentArrowUp,
        defaultConfig: {
          name: "Document Capture",
          slot: '',
          displayText: '',
          displayTextOptions: [],
          stateType: QUESTION_PROMPT,
          type: 'doc_capture',
          allowedFileTypes: ['pdf', 'docx', 'csv', 'txt', 'md', 'png', 'jpg'],
          extractionSchema: '',
          // The user has to upload on this turn, so there is nothing for entity
          // extraction to pre-fill and skip the step with.
          autoFillFromEntity: false,
          responseModifyByAI: false
        }
      },
      {
        id: LANGUAGE_SELECTOR_PROMPT_NODE_TYPE,
        text: "Language",
        icon: HiOutlineGlobeAlt,
        defaultConfig: {
          name: "Language Selector",
          slot: WORKFLOW_OPTION_SLOT,
          languages: [],
          stateType: QUESTION_PROMPT,
          type: 'languageSelector',
        }
      },
      // {
      //   id: CODEHOOK_PROMPT_NODE_TYPE,
      //   text: "Codehook",
      //   icon: MdConnectWithoutContact,
      // },
    ],
  },
  {
    id: "response",
    title: "Output",
    icon: HiOutlineChatBubbleLeftRight,
    nodes: [
      {
        id: MESSAGE_PROMPT_NODE_TYPE,
        text: "Text",
        icon: HiOutlineChatBubbleBottomCenterText,
        defaultConfig: {
          type: "text",
          name: "Message",
          displayText: '',
          displayTextOptions: [],
          responseModifyByAI: false
        }
      },
      {
        id: IMAGE_PROMPT_NODE_TYPE,
        text: "Image",
        icon: HiOutlinePhoto,
        defaultConfig: {
          type: "image",
          name: "Image",
          fileKey: ''
        }
      },
    ],
  },
  {
    id: "execute",
    title: "Actions",
    icon: HiOutlineBolt,
    nodes: [
      {
        id: AI_TASK_ACTION_NODE_TYPE,
        text: "AI Tasks",
        icon: HiOutlineSparkles,
        defaultConfig: {
          type: 'aiTask',
          name: "AI Task",
          systemPrompt: '',
          assignToSlots: {
            executors: [{
              inputPrompt: "",
              slotToAssign: ""
            }]
          }
        }
      },
      {
        id: DOC_SEARCH_ACTION_NODE_TYPE,
        text: "KB Query",
        icon: HiOutlineBookOpen,
        defaultConfig: {
          type: 'docSearch',
          name: "AI Knowledge",
          systemPrompt: '',
          inputPrompt: '',
          slotToAssign: '',
          skipNotify: false,
        }
      },
      {
        id: INTEGRATION_ACTION_NODE_TYPE,
        text: "Integration",
        icon: HiOutlineSquares2X2,
        defaultConfig: {
          name: "Integration",
          type: "integrationWorkflow",
          runtimeInput: {},
          integrationConfig: {},
        }
      },
      {
        id: LIVE_AGENT_ACTION_NODE_TYPE,
        text: "Human Transfer",
        icon: HiOutlineUserGroup,
        defaultConfig:  {
          name: "Human Agent",
          type: 'liveagent',
          journeyId: ''
        }
      },
      {
        id: AGENT_ACTION_NODE_TYPE,
        text: "Agent Action",
        icon: HiOutlineCpuChip,
        defaultConfig: {
          type: 'agentAction',
          name: "Agent Action",
          action: '',
        }
      }
    ],
  },
  {
    id: "dev",
    title: "Dev",
    icon: HiOutlineCommandLine,
    nodes: [
      {
        id: WEBHOOK_ACTION_NODE_TYPE,
        text: "API",
        icon: HiOutlineCloudArrowUp,
        defaultConfig: {
          name: "API",
          type: 'webhook',
          slotToAssign: '',
          skipNotify: true,
          pauseAfterExecution: false,
          defaultInput: '{}',
          webhookConfig: {
            url: '',
            method: 'POST',
            parameters: [],
            requestFormData: [],
            requestBody: '{}',
            requestType: 'raw',
            headers: [],
            responseMapping: [],
          },
          requestMapper: {
            type: 'inline_script',
            inlineScript: ''
          },
          responseMapper: {
            type: 'inline_script',
            inlineScript: ''
          },
          responseModifyByAI: false
        }
      },
      {
        id: CODEHOOK_ACTION_NODE_TYPE,
        text: "Function",
        icon: LuFunctionSquare,
        defaultConfig: {
          name: "Function",
          type: 'codehook',
          codehookId: '',
          slotToAssign: '',
          autoFillFromEntity: true,
          skipNotify: false,
          defaultInput: '{}',
          responseModifyByAI: false
        }
      },
      {
        id: AUTH_NODE_TYPE,
        text: "Authenticate",
        icon: HiOutlineShieldCheck,
        defaultConfig: {
          name: "Authenticate",
          type: 'auth',
          authConnector: '',
          prompt: '',
          defaultInput: '{}'
        }
      },
      {
        id: CUSTOM_ACTION_NODE_TYPE,
        text: "Custom Action",
        icon: HiOutlineCodeBracket,
        defaultConfig: {
          type: 'customAction',
          name: "Custom Action",
          id: '',
          slotToFill: ''
        }
      },
    ],
  },

  {
    id: "logic",
    title: "Flow",
    icon: HiOutlineArrowsRightLeft,
    nodes: [
      { id: CHOICE_NODE_TYPE, text: "Condition", icon: LuGitBranch },

      {
        id: PAUSE_ACTION_NODE_TYPE,
        text: "Pause",
        icon: HiOutlinePauseCircle,
        defaultConfig: {
          name: "Pause",
          type: 'pause',
        }
      },

      {
        id: JOURNEY_ACTION_NODE_TYPE,
        text: "Agent Run",
        icon: HiOutlineRectangleGroup,
        defaultConfig: {
          name: "Journey",
          type: 'journey',
          journeyId: ''
        }
      },
      // { id: CHOICE_NODE_TYPE, text: "Fallback", icon: TiDropbox },
    ],
  },
  {
    id: "variable",
    title: "Variable",
    icon: HiOutlineVariable,
    event: "variable",
    nodes: [],
  },
  // {
  //   id: "gen-ai-text-to-wf",
  //   title: "Generate",
  //   icon: HiOutlineSparkles,
  //   clickOnly: true,
  //   nodes: [
  //     {
  //       id: GEN_AI_TEXT_TO_WF,
  //       text: "Build from the scratch",
  //       icon: SparkIcon,
  //     },
  //     {
  //       id: GEN_AI_TEXT_TO_WF_EDIT,
  //       text: "Edit this Workflow",
  //       icon: EditSparkIcon,
  //     },
  //   ],
  // },
];
